import { computed, reactive, readonly } from 'vue'
import { getConfig } from '@/config'
import { apiRoutes } from '@/services/botApi'
import { getApiClient, type ApiRequestConfig } from '@/services/http'
import { isEmbeddedWindow } from '@/utils/windowContext'
import type { Router } from 'vue-router'

export interface AuthUser {
  sub: string
  org_id: string
  email?: string | null
  name?: string | null
}

interface StoredAuthSession {
  accessToken: string
  expiresAt: number
  user: AuthUser
}
export interface AuthSessionPayload {
  accessToken: string
  expiresAt: number
  user: AuthUser
}

interface CasExchangeResponse {
  accessToken: string
  expiresAt: number
  user: AuthUser
}

const AUTH_STORAGE_KEY = 'chat.auth.session'
const RETURN_TO_STORAGE_KEY = 'chat.auth.return_to'
const LAST_TICKET_STORAGE_KEY = 'chat.auth.last_ticket'
export const AUTH_FAILURE_REDIRECT_EVENT = 'chat:auth-failure-redirect'
interface AuthFailureRedirectEventDetail {
  status?: number
}
let suppressAutoLoginRedirect = false

const authState = reactive<{
  initialized: boolean
  loading: boolean
  isAuthenticated: boolean
  accessToken: string | null
  expiresAt: number | null
  user: AuthUser | null
}>({
  initialized: false,
  loading: false,
  isAuthenticated: false,
  accessToken: null,
  expiresAt: null,
  user: null,
})

function readSessionStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch (error) {
    logSessionStorageFailure('read', key, error)
    return null
  }
}

function writeSessionStorage(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value)
  } catch (error) {
    logSessionStorageFailure('write', key, error)
  }
}

function removeSessionStorage(key: string) {
  try {
    window.sessionStorage.removeItem(key)
  } catch (error) {
    logSessionStorageFailure('remove', key, error)
  }
}

function logSessionStorageFailure(
  operation: 'read' | 'write' | 'remove',
  key: string,
  error: unknown,
) {
  if (!import.meta.env.DEV) return
  console.warn(`[auth] Failed to ${operation} sessionStorage key "${key}"`, error)
}

function isStoredSessionValid(session: StoredAuthSession): boolean {
  return Boolean(session.accessToken && session.user?.sub && session.expiresAt * 1000 > Date.now())
}

function readStoredSession(): StoredAuthSession | null {
  const raw = readSessionStorage(AUTH_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredAuthSession
    return isStoredSessionValid(parsed) ? parsed : null
  } catch {
    return null
  }
}

function applySession(session: StoredAuthSession | null) {
  authState.isAuthenticated = Boolean(session)
  authState.accessToken = session?.accessToken ?? null
  authState.expiresAt = session?.expiresAt ?? null
  authState.user = session?.user ?? null
  if (session) {
    writeSessionStorage(AUTH_STORAGE_KEY, JSON.stringify(session))
  } else {
    removeSessionStorage(AUTH_STORAGE_KEY)
  }
}

function toStoredAuthSession(payload: AuthSessionPayload): StoredAuthSession {
  return {
    accessToken: payload.accessToken,
    expiresAt: payload.expiresAt,
    user: payload.user,
  }
}

function getCallbackServiceUrl(): string {
  return new URL('/auth/callback', window.location.origin).toString()
}

export function clearLoginFailures() {
  suppressAutoLoginRedirect = false
}

export function recordLoginFailure() {
  suppressAutoLoginRedirect = true
}

export function shouldSuppressAutoLoginRedirect(): boolean {
  return suppressAutoLoginRedirect
}

export function saveReturnTo(returnTo: string) {
  writeSessionStorage(RETURN_TO_STORAGE_KEY, returnTo)
}

export function peekReturnTo(): string | null {
  return readSessionStorage(RETURN_TO_STORAGE_KEY)
}

export function consumeReturnTo(): string | null {
  const value = readSessionStorage(RETURN_TO_STORAGE_KEY)
  removeSessionStorage(RETURN_TO_STORAGE_KEY)
  return value
}

export function hasProcessedTicket(ticket: string): boolean {
  return readSessionStorage(LAST_TICKET_STORAGE_KEY) === ticket
}

export function markTicketProcessed(ticket: string) {
  writeSessionStorage(LAST_TICKET_STORAGE_KEY, ticket)
}

export function clearProcessedTicket() {
  removeSessionStorage(LAST_TICKET_STORAGE_KEY)
}

export async function exchangeCasTicket(ticket: string, service = getCallbackServiceUrl()): Promise<void> {
  const payload = (await getApiClient().post<CasExchangeResponse>(
    apiRoutes.auth.exchangeCasTicket.path(),
    { ticket, service },
    { skipAuthRedirect: true } as ApiRequestConfig,
  )).data
  applySession({
    accessToken: payload.accessToken,
    expiresAt: payload.expiresAt,
    user: payload.user,
  })
  clearLoginFailures()
}

export async function initializeAuth(): Promise<void> {
  if (authState.initialized || authState.loading) return
  authState.loading = true
  try {
    const stored = readStoredSession()
    applySession(stored)
    authState.initialized = true
  } catch {
    applySession(null)
    authState.initialized = true
  } finally {
    authState.loading = false
  }
}

export function clearAuthSession() {
  applySession(null)
}

export async function logout(): Promise<void> {
  const token = authState.accessToken
  if (token) {
    await getApiClient().post(
      apiRoutes.auth.logout.path(),
      undefined,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        skipAuthRedirect: true,
      } as ApiRequestConfig,
    ).catch(() => undefined)
  }
  clearAuthSession()
  clearLoginFailures()
  clearProcessedTicket()
}

export function handleAuthorizationFailure(status: number) {
  if (status === 401) {
    clearAuthSession()
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<AuthFailureRedirectEventDetail>(
      AUTH_FAILURE_REDIRECT_EVENT,
      { detail: { status } },
    ))
  }
}

export function notifyAuthorizationRequired() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<AuthFailureRedirectEventDetail>(
      AUTH_FAILURE_REDIRECT_EVENT,
      { detail: { status: 401 } },
    ))
  }
}

export function registerAuthorizationFailureRedirect(router: Router) {
  if (typeof window === 'undefined') return

  let redirectInFlight = false
  const isEmbedded = isEmbeddedWindow()

  window.addEventListener(AUTH_FAILURE_REDIRECT_EVENT, async (event: Event) => {
    if (redirectInFlight) return
    if (isEmbedded) return

    const currentRoute = router.currentRoute.value
    if (currentRoute.matched.some(record => record.meta.authSurface === 'embedded')) {
      return
    }
    if (currentRoute.name === 'login' || currentRoute.name === 'auth-callback') {
      return
    }

    redirectInFlight = true
    try {
      const authFailureEvent = event as CustomEvent<AuthFailureRedirectEventDetail>
      const reason = authFailureEvent.detail?.status === 403 ? 'forbidden' : 'expired'
      saveReturnTo(currentRoute.fullPath)
      await router.replace({
        name: 'login',
        query: { reason },
      })
    } finally {
      redirectInFlight = false
    }
  })
}

export function getAccessToken(): string | null {
  if (authState.expiresAt && authState.expiresAt * 1000 <= Date.now()) {
    clearAuthSession()
    return null
  }
  return authState.accessToken
}

export function getAuthSessionPayload(): AuthSessionPayload | null {
  if (!authState.isAuthenticated || !authState.accessToken || !authState.expiresAt || !authState.user) {
    return null
  }

  return {
    accessToken: authState.accessToken,
    expiresAt: authState.expiresAt,
    user: {
      sub: authState.user.sub,
      org_id: authState.user.org_id,
      email: authState.user.email ?? null,
      name: authState.user.name ?? null,
    },
  }
}

export function applyAuthSessionPayload(payload: AuthSessionPayload): boolean {
  if (!isStoredSessionValid(toStoredAuthSession(payload))) {
    return false
  }

  applySession(toStoredAuthSession(payload))
  authState.initialized = true
  return true
}

export function getAuthDisplayName(): string {
  return authState.user?.name ?? authState.user?.email ?? authState.user?.sub ?? 'Guest'
}

export function getAuthLoginName(): string {
  return authState.user?.sub ?? authState.user?.email ?? 'Guest'
}

export function buildCasLoginUrl(): string {
  const { casBaseUrl, casLoginUrl } = getConfig()
  const configuredLoginUrl = casLoginUrl ?? (casBaseUrl ? `${casBaseUrl.replace(/\/$/, '')}/login` : null)
  if (!configuredLoginUrl) {
    throw new Error('CAS login is not configured')
  }
  const loginUrl = new URL(configuredLoginUrl)
  loginUrl.searchParams.set('service', getCallbackServiceUrl())
  return loginUrl.toString()
}

export function buildCasLogoutUrl(returnTo = new URL('/login', window.location.origin).toString()): string {
  const { casBaseUrl, casLogoutUrl } = getConfig()
  const configuredLogoutUrl = casLogoutUrl ?? (casBaseUrl ? `${casBaseUrl.replace(/\/$/, '')}/logout` : null)
  if (!configuredLogoutUrl) {
    throw new Error('CAS logout is not configured')
  }
  const logoutUrl = new URL(configuredLogoutUrl)
  logoutUrl.searchParams.set('service', returnTo)
  return logoutUrl.toString()
}

export function startCasLoginRedirect(returnTo?: string) {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const existingReturnTo = peekReturnTo()
  saveReturnTo(returnTo ?? existingReturnTo ?? currentPath)
  window.location.assign(buildCasLoginUrl())
}

export const authSession = readonly(authState)
export const isAuthenticated = computed(() => authState.isAuthenticated)
